import React, { useEffect, useMemo, useState } from "react";
import ErrorText from "../../commonComponents/ErrorTextContainer";
import "./style.css";
import "../../../styles/globals.css";

import { EMAIL_REGEX, notify } from "../../../utils/utils";
import {
  CENTRALISED_BILLING_API,
  STRIPE_PUBLISHABLE_KEY,
} from "../../../utils/envConfig";
import { apiCall } from "../../../utils/api";
import Select from "react-select";

import { loadStripe, Stripe, StripeCardElement } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import countryList from "react-select-country-list";
import Textfield from "../../InviteUserModal/Textfield";
import deleteBtnRed from "../../../Icons/delete-icon-red.svg";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

interface CountryOption {
  label: string; // Full name (e.g., "India")
  value: string; // ISO Code (e.g., "IN")
}

interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

function VerticalDivider() {
  return (
    <div className="h-8 relative shrink-0 w-0" data-name="Divider">
      <div className="absolute bottom-[-1.563%] left-[-0.5px] right-[-0.5px] top-[-1.563%]">
        <svg
          className="block size-full"
          fill="none"
          preserveAspectRatio="none"
          viewBox="0 0 2 34"
        >
          <g id="Divider">
            <path
              d="M1 1V33"
              id="V Divider"
              stroke="var(--stroke-0, #EAE7EC)"
              strokeLinecap="round"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="block cursor-pointer relative shrink-0 size-6 hover:opacity-70 transition-opacity"
      data-name="Close"
      onClick={onClick}
    >
      <svg
        className="block size-full"
        fill="none"
        preserveAspectRatio="none"
        viewBox="0 0 24 24"
      >
        <g id="Close">
          <path
            d="M18 6L6 18M6 6L18 18"
            id="Icon"
            stroke="var(--stroke-0, #211F26)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </g>
      </svg>
    </button>
  );
}

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function BillingDetailsForm({ subscription, workspaceId, onClose }) {
  const stripe = useStripe();
  const elements = useElements();

  const globalReducer = useSelector((state: any) => state.globalReducer);

  const options = useMemo<CountryOption[]>(() => countryList().getData(), []);

  const [loading, setLoading] = useState(false);
  const [cardUpdateLoading, setCardUpdateLoading] = useState(false);

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");
  const [country, setCountry] = useState<CountryOption | null>(null);
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
  });
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<any>(null);
  const [billing, setBilling] = useState<BillingDetails>({});
  const [customerCards, setCustomerCards] = useState<any[]>([]);

  useEffect(() => {
    if (subscription.stripeCustomerId) {
      fetchCustomerDetails(subscription.stripeCustomerId);
      fetchCustomerCardDetails(subscription.stripeCustomerId);
    }
  }, [subscription]);

  useEffect(() => {
    setBilling({
      name,
      email,
      phone,
      address: { ...address, country: country?.value || "" },
    });
  }, [name, email, phone, address, country]);

  // Fetch existing customer details from your backend or Stripe
  const fetchCustomerDetails = async (customerId: string) => {
    try {
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/customer/get-customer-info/${customerId}`
      );

      if (response) {
        const data = response.data;

        console.log("Fetched customer details:", data);

        setEmail(data.email || "");
        setName(data.name || "");
        setPhone(data.phone || "");
        const country = options.find((c) => c.value === data.address?.country);
        setCountry(country || null);
        setAddress({
          line1: data.address?.line1 || "",
          line2: data.address?.line2 || "",
          city: data.address?.city || "",
          state: data.address?.state || "",
          postal_code: data.address?.postal_code || "",
          country: data.address?.country || "",
        });

        if (data.invoice_settings?.default_payment_method) {
          // setDefaultPaymentMethod(data.invoice_settings.default_payment_method);
          setDefaultPaymentMethod(data.invoice_settings.default_payment_method);
        }
      } else {
        console.error("Failed to fetch customer details");
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
    }
  };

  const fetchCustomerCardDetails = async (customerId: string) => {
    try {
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/customer/customer-cards/${customerId}`
      );

      if (response) {
        const data = response.data;

        console.log("Fetched customer card details:", data);

        if (data) {
          setCustomerCards(data);
        }
      } else {
        console.error("Failed to fetch customer card details");
      }
    } catch (error) {
      console.error("Error fetching customer card details:", error);
      notify("Error fetching customer card details", "error");
    }
  };

  const handleChange = (value: CountryOption | null) => {
    console.log("Selected country:", value);
    setAddress((prev) => ({ ...prev, country: value ? value.value : "" }));
    setCountry(value);
  };

  const handleUpdateCustomerDetails = async () => {
    let isValid = true;

    // Email validation

    if (!EMAIL_REGEX.test(email)) {
      setEmailError("Please enter a valid email address.");
      isValid = false;
    }

    // Name validation
    // if (name.trim() === "") {
    //   setNameError("Name cannot be empty.");
    //   isValid = false;
    // }

    // Phone validation
    // if (phone && (isNaN(Number(phone)) || phone.length !== 10)) {
    //   setPhoneError("Phone number must be 10 digits.");
    //   isValid = false;
    // }

    if (!isValid) {
      return;
    }

    const customerData = {
      email,
      name,
      phone: phone || undefined,
      address: {
        line1: address.line1 || undefined,
        line2: address.line2 || undefined,
        city: address.city || undefined,
        state: address.state || undefined,
        postal_code: address.postal_code || undefined,
        country: address.country || undefined,
      },
    };
    setLoading(true);
    try {
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/customer/update-customer-info/${subscription.stripeCustomerId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(customerData),
        }
      );
      setLoading(false);

      if (response) {
        // Successfully updated customer details
        setLoading(false);
        notify("Customer details updated successfully", "success");
        onClose();
      } else {
        setLoading(false);
        notify("Failed to update customer details", "error");
        console.error("Failed to update customer details");
      }
    } catch (error) {
      setLoading(false);
      notify("Failed to update customer details", "error");
      console.error("Error updating customer details:", error);
    }
  };

  const handleCardUpdate = async () => {
    if (
      !address.line1 ||
      !address.city ||
      !address.postal_code ||
      !address.country
    ) {
      alert(
        "Please fill in the required address fields (line1, city, postal code, country) before updating the card."
      );
      return;
    }

    if (!stripe || !elements) {
      console.error("Stripe.js has not yet loaded.");
      return;
    }

    const c = confirm("Are you sure to update card details");

    if (!c) {
      return;
    }

    setCardUpdateLoading(true);
    const cardElement = elements.getElement(CardElement) as StripeCardElement;

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: { ...billing, address },
    });

    console.log("Payment Method:", paymentMethod);

    if (error) {
      alert(error.message);
      setCardUpdateLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${CENTRALISED_BILLING_API}/customer/update-customer-card/${subscription.stripeCustomerId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
            billingDetails: { ...billing, address },
            workspaceId,
          }),
        }
      );

      // Throw if HTTP error
      if (!response.ok) {
        let errorMsg = "Failed to update card";
        try {
          const errorData = await response.json();
          console.log(errorData);

          if (
            errorData.error &&
            errorData.error.raw &&
            errorData.error.raw.message
          ) {
            notify(errorData.error.raw.message, "error");
          } else {
            throw new Error(errorMsg);
          }

          // if(errorData.)
        } catch (e) {
          // ignore JSON parse error
        }
      }

      const data = await response.json();
      setCardUpdateLoading(false);

      if (data.success) {
        handleUpdateCustomerDetails();
        notify("Card updated successfully!");
      } else {
        notify(data.error || "Failed to update card", "error");
      }

      onClose();
    } catch (err) {
      console.log("Error: ", err, typeof err);
      setCardUpdateLoading(false);

      let errorMsg = "Unable to add card details";
      if (err && typeof err === "object") {
        console.log(JSON.stringify(err));
      }
      notify(errorMsg, "error");
      onClose();
    }

    setCardUpdateLoading(false);
  };

  const handleRemoveCard = async (cardId: string) => {
    const c = confirm("Are you sure to remove the saved card");
    if (!c) {
      return;
    }

    try {
      const response = await apiCall(
        `${CENTRALISED_BILLING_API}/customer/customer-card/user/${globalReducer.userData._id}/customer/${subscription.stripeCustomerId}/card/${cardId}`,
        { method: "DELETE" }
      );

      if (response) {
        const data = response.data;

        console.log("Fetched customer card details:", data);

        if (data.deleted) {
          fetchCustomerCardDetails(subscription.stripeCustomerId);
          notify("Card removed successfully");
        }
      } else {
        throw new Error("Unable to remove card");
      }
    } catch (error) {
      console.log("Error: ", error);
      notify("Failed to delete card", "error");
    }
  };

  const getFlagEmoji = (countryCode: string) =>
    countryCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt(0))
      );

  return (
    <div className="relative h-screen w-full" data-name="Invite User Modal">
      <div
        className="absolute box-border content-stretch flex flex-col gap-2 items-center justify-end pb-0 pt-14 pl-14 pr-14 top-0 w-[1000px]"
        data-name="Modal"
        style={{
          paddingLeft: "56px",
          paddingRight: "56px",
          paddingTop: "56px",
          right: "calc(50% - 500px)",
          maxHeight: "90vh",
          // minHeight: "50%",
        }}
      >
        <div
          className="backdrop-blur-md backdrop-filter bg-[rgba(255,255,255,0.72)] grow min-h-px min-w-px relative rounded-[24px] shadow-[-4px_0px_24px_0px_rgba(0,0,0,0.02)] w-full overflow-auto"
          data-name="Container"
        >
          <div className="overflow-clip relative size-full flex flex-col">
            <div
              className="box-border content-stretch flex flex-row gap-6 items-center justify-center p-0 relative shrink-0 w-full p-[24px]"
              data-name="Header"
            >
              <div
                className="box-border content-stretch flex flex-row gap-6 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0"
                data-name="Header"
              >
                <div className="font-['Geist:Medium',_sans-serif] font-medium leading-[0] relative shrink-0 text-[#211f26] text-[24px] text-left text-nowrap tracking-[-0.24px]">
                  <p className="adjustLetterSpacing block leading-[30px] whitespace-pre">
                    Update Billing Details
                  </p>
                </div>
                {/* <VerticalDivider /> */}
              </div>
              <CloseButton onClick={onClose} />
            </div>

            <div className="customer-details-body">
              <div
                className="sub-heading"
                style={{ margin: "0", padding: "0" }}
              >
                Billing Details:
              </div>

              <div className="customer-info-input">
                <div className="input-tab">
                  <Textfield
                    label="Billing Email"
                    value={email}
                    onChange={(value) => setEmail(value)}
                    placeholder="Enter your email"
                    type="email"
                  />
                  {emailError && <ErrorText hasError={emailError}></ErrorText>}
                </div>

                {/* <div className="input-tab">
                  <Textfield
                    label="Name"
                    value={name}
                    onChange={(value) => setName(value)}
                    placeholder="Enter your name"
                    type="text"
                  />
                  {nameError && <ErrorText hasError={nameError}></ErrorText>}
                </div> */}
                {/* <div className="input-tab">
                  <Textfield
                    label="Phone No."
                    value={phone}
                    onChange={(value) => {
                      if (!isNaN(Number(value)) && value.length <= 10) {
                        setPhone(value);
                      }
                      if (value.length > 10) {
                        setPhoneError("Phone number must be 10 digits");
                      } else {
                        setPhoneError("");
                      }
                    }}
                    placeholder="Enter your phone number"
                    type="number"
                  />
                  {phoneError && <ErrorText hasError={phoneError}></ErrorText>}
                </div> */}
                {/* </div> */}
                {/* <div className="sub-heading">Address:</div> */}

                {/* <div className="customer-info-input"> */}
                <div className="input-tab">
                  <Textfield
                    label="Address Line 1"
                    value={address.line1}
                    onChange={(value) =>
                      setAddress((prev) => ({ ...prev, line1: value }))
                    }
                    placeholder="Enter Address Line 1"
                    type="text"
                  />
                </div>
                <div className="input-tab">
                  <Textfield
                    label="Address Line 2"
                    value={address.line2}
                    onChange={(value) =>
                      setAddress((prev) => ({ ...prev, line2: value }))
                    }
                    placeholder="Enter Address Line 2"
                    type="text"
                  />
                </div>
                <div className="input-tab">
                  <Textfield
                    label="City"
                    value={address.city}
                    onChange={(value) =>
                      setAddress((prev) => ({ ...prev, city: value }))
                    }
                    placeholder="Enter City"
                    type="text"
                  />
                </div>
                <div className="input-tab">
                  <Textfield
                    label="State"
                    value={address.state}
                    onChange={(value) =>
                      setAddress((prev) => ({ ...prev, state: value }))
                    }
                    placeholder="Enter State"
                    type="text"
                  />
                </div>
                <div className="input-tab">
                  <Textfield
                    label="Postal Code"
                    value={address.postal_code}
                    onChange={(value) =>
                      setAddress((prev) => ({ ...prev, postal_code: value }))
                    }
                    placeholder="Enter Postal Code"
                    type="number"
                  />
                </div>
                <div className="input-tab">
                  <label>Country</label>
                  <Select
                    options={options}
                    value={country}
                    onChange={handleChange}
                    placeholder="Select a country"
                    isClearable
                    getOptionLabel={(option) =>
                      `${getFlagEmoji(option.value)} ${option.label}`
                    }
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "8px",
                        padding: "2px",
                        borderColor: "#ccc",
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="add-btn-container">
                <button
                  className="add-customer"
                  onClick={() => {
                    const c = confirm("Are you sure to update details");
                    if (!c) {
                      return;
                    }
                    handleUpdateCustomerDetails();
                  }}
                  style={{}}
                >
                  Save Changes
                </button>
              </div>

              <div className="sub-heading">Card Details:</div>

              {customerCards && customerCards.length > 0 ? (
                <div className="saved-card-details">
                  Saved cards:
                  <div className="cards-wrapper">
                    {customerCards.map((card) => (
                      <div key={card.id} className="saved-card">
                        <div className="card-number">
                          <div className="card-digits">XXXX{card.last4}</div>
                          <div>
                            {card.exp_month}/{card.exp_year}
                          </div>
                        </div>
                        {card.is_default && (
                          <div className="default-card">Default</div>
                        )}
                        <div
                          className="delete-card-btn"
                          onClick={() => {
                            if (card.is_default) {
                              notify(
                                "Can not removed default card, Add another card to remove this card",
                                "error"
                              );
                              return;
                            }
                            handleRemoveCard(card.id);
                          }}
                        >
                          <img src={deleteBtnRed} alt="delete btn" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>No saved cards found.</div>
              )}

              <div className="customer-info-input card-update-section">
                <div className="">
                  Enter your card details:
                  <CardElement />
                </div>
                <button
                  onClick={handleCardUpdate}
                  disabled={cardUpdateLoading}
                  className="add-customer"
                >
                  {cardUpdateLoading ? "Updating..." : "Update Card"}
                </button>
              </div>
            </div>

            {/* <div className="customer-details-footer">
             
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingDetailsModal({
  subscription,

  onClose,
}) {
  const params = useParams();
  const workspaceId = params.workspaceId as string;

  return (
    <Elements stripe={stripePromise}>
      <BillingDetailsForm
        subscription={subscription}
        workspaceId={workspaceId}
        onClose={onClose}
      />
    </Elements>
  );
}
